import { defineMutator } from "@rocicorp/zero"

import type { ZeroJwtPayload } from "@productlane/lib/src/browser"

import type { ChangelogTag, MutatorTx, Schema } from "../schema"
import type { CreateValue } from "../types"
import type { DeleteID, UpdateValue } from "@rocicorp/zero"

export const changelogTagMutators = {
	create: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: CreateValue<ChangelogTag>
			ctx: ZeroJwtPayload
		}) => {
			await tx.mutate.changelogTags.insert({
				id: args.id,
				name: args.name,
				color: args.color,
				icon: args.icon ?? null,
				createdAt: Date.now(),
				updatedAt: Date.now(),
				workspaceId: authData.workspaceId,
			})
		},
	),
	update: defineMutator(
		async ({
			tx,
			args,
		}: {
			tx: MutatorTx
			args: UpdateValue<Schema["tables"]["changelogTags"]>
		}) => {
			await tx.mutate.changelogTags.update({
				id: args.id,
				name: args.name,
				color: args.color,
				icon: args.icon,
			})
		},
	),
	delete: defineMutator(
		async ({
			tx,
			args,
		}: {
			tx: MutatorTx
			args: DeleteID<Schema["tables"]["changelogTags"]>
		}) => {
			// Remove tag from all changelogs first
			const changelogsWithTag = await tx.query.changelogs
				.whereExists("changelogTags", (t) => t.where("id", args.id))
				.run()

			await Promise.all(
				changelogsWithTag.map((changelog) =>
					tx.mutate.changelogToChangelogTags.delete({
						A: changelog.id,
						B: args.id,
					}),
				),
			)

			await tx.mutate.changelogTags.delete({ id: args.id })
		},
	),
	linkTag: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: { changelogId: string; tagId: string }
			ctx: ZeroJwtPayload
		}) => {
			const [changelog] = await tx.query.changelogs
				.where("id", "=", args.changelogId)
				.where("workspaceId", "=", authData.workspaceId)
				.run()

			if (!changelog) {
				throw new Error("Changelog not found")
			}

			const [tag] = await tx.query.changelogTags
				.where("id", "=", args.tagId)
				.where("workspaceId", "=", authData.workspaceId)
				.run()

			if (!tag) {
				throw new Error("Changelog tag not found")
			}

			const [existingLink] = await tx.query.changelogToChangelogTags
				.where("A", "=", args.changelogId)
				.where("B", "=", args.tagId)
				.run()

			if (existingLink) {
				await tx.mutate.changelogToChangelogTags.delete({
					A: args.changelogId,
					B: args.tagId,
				})
				return
			}

			await tx.mutate.changelogToChangelogTags.insert({
				A: args.changelogId,
				B: args.tagId,
			})
		},
	),
	unlinkTag: defineMutator(
		async ({
			tx,
			args,
		}: {
			tx: MutatorTx
			args: { changelogId: string; tagId: string }
		}) => {
			const relationships = await tx.query.changelogToChangelogTags
				.where("A", "=", args.changelogId)
				.where("B", "=", args.tagId)
				.run()

			for (const relationship of relationships) {
				await tx.mutate.changelogToChangelogTags.delete({
					A: relationship.A,
					B: relationship.B,
				})
			}
		},
	),
}
